import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useCfarV2StateForCheckout } from "@b2bportal/core-flights-cfar-offers";
import {
  AirCancelForAnyReasonStateV2,
  CheckoutMapComponent,
  ParentState,
  ResumeEvent,
  useCheckoutSend,
} from "@hopper-b2b/checkout";
// eslint-disable-next-line @nx/enforce-module-boundaries
import { shopSelectors } from "@hopper-b2b/flights";

import { BagsAndSeatsSelection } from "../../../checkout/components/BagsAndSeatsSelection";
import { LoadingScreen } from "../../../checkout/components/LoadingScreen";
import { NubankPaymentComponent } from "../../../checkout/components/NubankPayment";
import type { TEvent } from "../events";
import { useUpdateCheckoutUrl } from "../useUpdateCheckoutUrl";
import { BaggageSelection } from "./BaggageSelection";
import { BookingConfirmation } from "./BookingConfirmation";
import { CartFulfillComponent } from "./CartFulfillComponent";
import { CartQuoteComponent } from "./CartQuoteComponent";
import { CartUpdateComponent } from "./CartUpdateComponent";
import { ConnectedContactInformation as ContactInformation } from "./ContactInformation";
import { ConnectedPassengerInformation as PassengerInformation } from "./PassengerInformation";
import { PassportInformation } from "./PassportInformation";
import { RefreshCheckout } from "./RestartCheckout";
import { ConnectedReviewComponent as ReviewComponent } from "./Review";
import { ConnectedSeatSelection as SeatSelection } from "./SeatSelection";

export const flightComponentMap = {
  [ParentState.resume]: {
    state: ParentState.resume,
    component: RefreshCheckout,
  },
  [ParentState.loading]: {
    state: ParentState.loading,
    component: LoadingScreen,
  },
  [ParentState.contactInformation]: {
    state: ParentState.contactInformation,
    component: ContactInformation,
  },
  [ParentState.passengerInformation]: {
    state: ParentState.passengerInformation,
    component: PassengerInformation,
  },
  [ParentState.passport]: {
    state: ParentState.passport,
    component: PassportInformation,
  },
  [ParentState.cartQuote]: {
    state: ParentState.cartQuote,
    component: CartQuoteComponent,
  },
  [ParentState.cartUpdate]: {
    state: ParentState.cartUpdate,
    component: CartUpdateComponent,
  },
  bagsAndSeats: {
    state: "bagsAndSeats",
    component: BagsAndSeatsSelection,
  },
  [ParentState.bagsSelection]: {
    state: ParentState.bagsSelection,
    component: BaggageSelection,
  },
  [ParentState.seatSelection]: {
    state: ParentState.seatSelection,
    component: SeatSelection,
  },
  [ParentState.review]: {
    state: ParentState.review,
    component: ReviewComponent,
  },
  [ParentState.cartUpdateBeforeFulfill]: {
    state: ParentState.cartUpdateBeforeFulfill,
    component: CartUpdateComponent,
  },
  [ParentState.payment]: {
    state: ParentState.payment,
    component: NubankPaymentComponent,
  },
  [ParentState.cartFulfill]: {
    state: ParentState.cartFulfill,
    component: CartFulfillComponent,
  },
  [ParentState.bookingConfirmation]: {
    state: ParentState.bookingConfirmation,
    component: BookingConfirmation,
  },
};

export const FlightCheckoutComponent = () => {
  useUpdateCheckoutUrl();

  const send = useCheckoutSend<TEvent>();

  const cfarState = useCfarV2StateForCheckout();

  // Get price drop candidate ID from Redux (shop state) and set it in xstate context
  // This ensures candidateId is always set, regardless of any timing issues between
  // when FlightsCheckout mounts and when the XState machine context is initialized.
  // The useEffect will fire whenever the value is available from Redux.
  const priceDropProtectionCandidateId = useSelector(
    shopSelectors.priceDropProtectionCandidateIdSelector
  );

  useEffect(() => {
    send({
      type: AirCancelForAnyReasonStateV2.initialize,
      state: cfarState,
    });
  }, []);

  // Initialize price drop context with candidate ID from shop
  useEffect(() => {
    if (priceDropProtectionCandidateId) {
      send({
        type: ResumeEvent.SET_PRICE_DROP_CONTEXT,
        candidateId: priceDropProtectionCandidateId,
      } as TEvent);
    }
  }, [priceDropProtectionCandidateId, send]);

  return <CheckoutMapComponent componentMap={flightComponentMap} />;
};
